import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { CountryRow } from '../../types/census';
import { CountryDataState } from '../../hooks/useCountryData';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { agentsPerCapitaFor, countryMachines, ENDPOINT_FORMULA } from '../../utils/estimates';
import { useScenario } from '../../contexts/ScenarioContext';
import { formatCompact, formatInt, formatPct } from '../../utils/format';
import { SourceRef } from '../SourceRef';
interface CountryPanelProps {
  row: CountryRow | null;
  modes: CountryDataState['modes'];
  onClose: () => void;
}
export function CountryPanel({
  row,
  modes,
  onClose
}: CountryPanelProps) {
  const desktop = useMediaQuery('(min-width: 768px)');
  const {
    scenarioId,
    scenario
  } = useScenario();
  useEffect(() => {
    if (!row) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [row, onClose]);
  const offset = desktop ? {
    x: 32,
    y: 0
  } : {
    x: 0,
    y: 40
  };
  return <AnimatePresence data-id="element-464" data-mp="166flk7v77u6t">
      {row && <motion.aside key={row.cca3} role="dialog" aria-label={`${row.name} details`} initial={{
      opacity: 0,
      ...offset
    }} animate={{
      opacity: 1,
      x: 0,
      y: 0
    }} exit={{
      opacity: 0,
      ...offset
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-x-0 bottom-0 z-50 max-h-[78vh] overflow-y-auto rounded-t-xl border-t border-line bg-surface shadow-2xl shadow-black/70 md:inset-x-auto md:bottom-0 md:right-0 md:top-0 md:max-h-none md:w-[380px] md:rounded-none md:border-l md:border-t-0" data-id="element-465" data-mp="1678mutbe1rhk">
          <div className="sticky top-0 flex items-center justify-between gap-3 border-b border-line bg-surface px-5 py-4" data-id="element-466" data-mp="3qxg4ymx68rro">
            <div className="flex min-w-0 items-center gap-3" data-id="element-467" data-mp="1ptnebyboiocc">
              <img src={row.flag} alt={`Flag of ${row.name}`} className="h-6 w-9 shrink-0 rounded-sm object-cover ring-1 ring-line" data-id="element-468" data-mp="1pvi0th2lmoij" />
              <div className="min-w-0" data-id="element-469" data-mp="1zrtdw9a0jie1">
                <h2 className="truncate text-lg text-ink" data-id="element-470" data-mp="1zqrzpvxpafs3">{row.name}</h2>
                <p className="font-mono text-[11px] text-muted" data-id="element-471" data-mp="23teykgssh0ys">
                  {row.cca3} · Capital {row.capital}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close panel" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line text-muted hover:text-ink" data-id="element-472" data-mp="2drcl8zv02ppa">
              <XIcon className="h-4 w-4" data-id="element-473" data-mp="2ngw6uda3kxnu" />
            </button>
          </div>

          <dl className="divide-y divide-line px-5" data-id="element-474" data-mp="2niyuiqns2epe">
            <Stat label="Population" value={formatInt(row.population)} sourceId="restcountries" hint={modes.countries} data-id="element-475" data-mp="2rjsh9gphhngp" />
            <Stat label="Internet users" value={row.internetPct ? `${formatPct(row.internetPct.value)} · ${formatCompact(row.internetUsers ?? 0)}` : '—'} sub={row.internetPct ? `World Bank, ${row.internetPct.year}` : 'Not reported'} sourceId="wbInternet" hint={modes.internet} data-id="element-476" data-mp="2widlrsbrqtay" />
            <Stat label="Mobile subscriptions" value={row.mobilePer100 ? `${row.mobilePer100.value.toFixed(1)} per 100` : '—'} sub={row.mobilePer100 ? `World Bank, ${row.mobilePer100.year}` : 'Not reported'} sourceId="wbMobile" hint={modes.mobile} data-id="element-477" data-mp="6vhd6nfltmc8" />
            <Stat label="Median age" value={row.medianAge != null ? `${row.medianAge.toFixed(1)} years` : '—'} sub="UN WPP 2024" sourceId="unwpp" hint="snapshot" data-id="element-478" data-mp="ur3ngm39owbh" />
            <Stat label="Working-age share (15–64)" value={row.workingAgePct ? formatPct(row.workingAgePct.value) : '—'} sub={row.workingAgePct ? `World Bank, ${row.workingAgePct.year}` : 'Not reported'} sourceId="wbWorking" hint={modes.working} data-id="element-479" data-mp="1n9h79hrrqhrk" />
          </dl>

          <div className="mx-5 my-5 rounded-md border border-machine/30 bg-machine/[0.06] p-4" data-id="element-480" data-mp="1734et7vt06yl">
            <p className="font-mono text-[10.5px] uppercase tracking-wider text-machine" data-id="element-481" data-mp="t3way1op4ao2">Our estimate</p>
            <p className="mt-1 text-[13px] text-ink" data-id="element-482" data-mp="y1ejhqyswnvj">Device-assistant endpoints</p>
            <p className="mt-2 font-mono text-2xl tabular text-machine" data-id="element-483" data-mp="1lr8z2gai8qjh">
              {row.endpoints != null ? formatCompact(row.endpoints) : '—'}
              <SourceRef id="censusModel" estimate={row.endpoints != null ? {
            low: formatCompact(row.endpoints * 0.85),
            high: formatCompact(row.endpoints * 1.15)
          } : undefined} data-id="element-484" data-mp="1c15tzhq3zm4s" />
            </p>
            <p className="mt-2 break-words font-mono text-[11px] leading-relaxed text-muted" data-id="element-485" data-mp="24pl53t64dkau">{ENDPOINT_FORMULA}</p>
            <div className="mt-4 border-t border-machine/20 pt-3" data-id="element-486" data-mp="29gtjl3mb3vuv">
              <p className="text-[13px] text-ink" data-id="element-487" data-mp="29i4ml17xgzkx">Machines · {scenario.label}</p>
              {(() => {
            const m = countryMachines(row, scenarioId);
            const pc = agentsPerCapitaFor(row, scenarioId);
            return <p className="mt-1 font-mono text-[15px] tabular text-machine" data-id="element-488" data-mp="2sjlkljhvvijq">
                    {m != null ? formatCompact(m) : '—'}
                    <span className="ml-2 text-[12px] text-muted" data-id="element-489" data-mp="2no68ox3frwpy">{pc != null ? `${pc < 0.1 ? pc.toFixed(4) : pc.toFixed(2)} per person` : ''}</span>
                    <SourceRef id="censusModel" estimate={m != null ? {
                low: formatCompact(m * 0.8),
                high: formatCompact(m * 1.25)
              } : undefined} data-id="element-490" data-mp="1vvpdr6c901lp" />
                  </p>;
          })()}
            </div>
          </div>

          <div className="px-5 pb-6" data-id="element-491" data-mp="24xf3m7qheshd">
            <Link to={`/countries/${row.cca3}`} className="inline-flex w-full items-center justify-center rounded-md bg-ink px-4 py-2.5 text-sm font-medium text-bg transition-opacity duration-150 hover:opacity-90" data-id="element-492" data-mp="3l92kt3bqw3qi">
              View full country page →
            </Link>
          </div>
        </motion.aside>}
    </AnimatePresence>;
}
interface StatProps {
  label: string;
  value: string;
  sub?: string;
  sourceId: string;
  hint: 'live' | 'snapshot';
}
function Stat({
  label,
  value,
  sub,
  sourceId,
  hint
}: StatProps) {
  return <div className="py-3.5" data-id="element-493" data-mp="6527uclm0yt4">
      <dt className="flex items-center justify-between text-[12.5px] text-muted" data-id="element-494" data-mp="34n7qhsn1gmns">
        {label}
        <span className={`font-mono text-[10px] ${hint === 'live' ? 'text-live' : 'text-machine/80'}`} data-id="element-495" data-mp="39makmi1hd8gz">{hint}</span>
      </dt>
      <dd className="mt-1 font-mono text-[15px] tabular text-ink" data-id="element-496" data-mp="3ekvlmjok3w6h">
        {value}
        <SourceRef id={sourceId} data-id="element-497" data-mp="3jbu51s4udady" />
      </dd>
      {sub && <dd className="font-mono text-[11px] text-faint" data-id="element-498" data-mp="3jdopz6g452i1">{sub}</dd>}
    </div>;
}